/**
 * MUADemoContent - Exact replica of the MUA landing page content
 * This component renders the same content as MUASalesPage but is designed
 * to be embedded in other pages (like the invite page demo tab)
 * 
 * This is a standalone component without router dependencies
 * All color blocks/placeholders are now editable images for super admin
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAdmin, EditableText, EditableImage, EditableSection } from "@/components/AdminContentEditor";
import {
  Sparkles,
  ArrowRight,
  Calendar,
  MessageCircle,
  Star,
  Camera,
  Users,
  Clock,
  CheckCircle2,
  Instagram,
  Shield,
  Award,
  Zap,
  Palette,
  Heart,
  Smartphone,
  CreditCard,
  ClipboardList,
  Send,
  Image as ImageIcon
} from "lucide-react";

export default function MUADemoContent({ onAccept, portfolioImages = [] }) {
  const { editMode } = useAdmin();
  
  // Feature highlights
  const features = [
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "Travel time calculator, kit prep reminders, and buffer time built in"
    },
    {
      icon: ClipboardList,
      title: "Client Intake",
      description: "Skin type, allergies, inspiration photos - all collected before arrival"
    },
    {
      icon: Camera,
      title: "Portfolio Builder",
      description: "Auto-watermark, before/after galleries, and IG content generator"
    },
    {
      icon: CreditCard,
      title: "Payment Tracking",
      description: "Deposits, tips, invoices - never lose track of what you're owed"
    },
    {
      icon: MessageCircle,
      title: "Client Communication",
      description: "Automated reminders, day-of details, and thank-you messages"
    },
    {
      icon: Star,
      title: "Review Collection",
      description: "Automatic review requests timed perfectly after service"
    }
  ];

  // Look/style options with editable images
  const lookOptions = [
    { id: "soft-glam", name: "Soft Glam", default: null },
    { id: "natural", name: "Natural", default: null },
    { id: "bold", name: "Bold", default: null },
    { id: "bridal", name: "Bridal", default: null },
    { id: "editorial", name: "Editorial", default: null },
    { id: "everyday", name: "Everyday", default: null }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Hero Section */}
      <section className="pt-16 pb-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Built for Makeup Artists</span>
          </div>
          
          <EditableText
            sectionId="mua-demo-hero"
            field="title"
            defaultValue="The Ultimate Client Experience Platform for MUAs"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-900"
            as="h1"
          />
          
          <EditableText
            sectionId="mua-demo-hero"
            field="subtitle"
            defaultValue="From first inquiry to rebooking, create a seamless experience that turns one-time clients into loyal fans."
            className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            as="p"
          />
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onAccept}
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white h-14 px-8 rounded-full text-lg"
            >
              Join the Pilot Program
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-14 px-8 rounded-full text-lg border-2"
            >
              See How It Works
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Free during pilot</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Limited spots available</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span>Shape the platform</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Sound Familiar?</h2>
              <ul className="space-y-4">
                {[
                  "Spending hours on back-and-forth messages to book one appointment",
                  "Showing up without knowing client's skin type or allergies",
                  "Forgetting to follow up after services",
                  "Losing track of deposits and payments",
                  "No system to collect reviews or showcase work"
                ].map((problem, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span className="text-gray-700">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">We've Got You</h2>
              <ul className="space-y-4">
                {[
                  "One-tap booking with automatic confirmations",
                  "Pre-appointment intake forms with all details",
                  "Automated reminders and follow-ups",
                  "Built-in payment and tip tracking",
                  "Automatic review requests and portfolio building"
                ].map((solution, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                    <span className="text-gray-700">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Share Your Look Section - EDITABLE IMAGES */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <EditableText
              sectionId="mua-demo-looks"
              field="title"
              defaultValue="How Clients Share Their Look"
              className="text-3xl font-bold mb-4"
              as="h2"
            />
            <EditableText
              sectionId="mua-demo-looks"
              field="subtitle"
              defaultValue="Pick from your portfolio or let clients add their own inspo"
              className="text-gray-600 max-w-2xl mx-auto"
              as="p"
            />
          </div>

          {/* Editable Look Grid - Uses invite-specific images if provided */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {lookOptions.map((look, idx) => {
              const lookImg = portfolioImages[idx + 4]; // Use images 5-10 for looks
              return (
                <div key={look.id} className="group relative">
                  {lookImg ? (
                    <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                      <img 
                        src={lookImg} 
                        alt={look.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <EditableImage
                      sectionId="mua-demo-looks"
                      field={`look_${look.id}`}
                      defaultImage={null}
                      className="aspect-square rounded-xl overflow-hidden"
                      placeholderClassName="bg-gradient-to-br from-purple-200 to-indigo-200"
                      placeholderIcon={<ImageIcon className="w-8 h-8 text-purple-400" />}
                    />
                  )}
                  <div className="absolute bottom-2 left-2 right-2">
                    <span className="text-xs font-medium bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                      {look.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Portfolio Showcase - Uses invite-specific images if provided */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-pink-500" />
              Your Portfolio Showcase
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((idx) => {
                const portfolioImg = portfolioImages[idx];
                return portfolioImg ? (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={portfolioImg} 
                      alt={`Portfolio ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <EditableImage
                    key={idx}
                    sectionId="mua-demo-portfolio"
                    field={`portfolio_${idx + 1}`}
                    defaultImage={null}
                    className="aspect-square rounded-lg overflow-hidden"
                    placeholderClassName="bg-gradient-to-br from-purple-100 to-indigo-100"
                    placeholderIcon={<Camera className="w-6 h-6 text-purple-400" />}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Everything You Need to Elevate Your Business</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Built specifically for mobile makeup artists who want to deliver a premium client experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Journey Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Your Client's Journey</h2>
            <p className="text-gray-600">See how phygital transforms every touchpoint</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {[
              { step: "1", title: "Discovery", desc: "Client finds you, sees your work, and books instantly", icon: Heart },
              { step: "2", title: "Preparation", desc: "Automated intake collects preferences and requirements", icon: ClipboardList },
              { step: "3", title: "Service Day", desc: "Address reveal, reminders, and seamless check-in", icon: Palette },
              { step: "4", title: "Follow-up", desc: "Thank you, review request, and rebooking nudge", icon: Send }
            ].map((phase, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white rounded-2xl p-6 border shadow-sm h-full">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white flex items-center justify-center font-bold mb-4">
                    {phase.step}
                  </div>
                  <phase.icon className="w-8 h-8 text-purple-500 mb-3" />
                  <h3 className="font-semibold mb-2">{phase.title}</h3>
                  <p className="text-gray-600 text-sm">{phase.desc}</p>
                </div>
                {idx < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-purple-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Client Experience?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Join the pilot program and help shape the future of phygital for makeup artists. 
            Limited spots available.
          </p>
          <Button
            onClick={onAccept}
            size="lg"
            className="bg-white text-purple-600 hover:bg-gray-100 h-14 px-10 rounded-full text-lg font-semibold"
          >
            Accept Your Invite
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="mt-4 text-white/70 text-sm">
            Free during pilot • No credit card required • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
}
